import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tooltip,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import * as XLSX from 'xlsx';
import BusyOverlay from '../commonComponents/BusyOverlay';
import {
    compactIconButtonSx,
    compactPanelPaddingSx,
    compactSelectSx,
} from './tableStyles';
import { outputHeatmapClassName } from './outputHeatmapClasses';
import { formatValue } from './positionSizingFormat';
import CompactPanelHeader from '../commonComponents/CompactPanelHeader';
import { cardClasses } from '../../styles/tailwindClasses';
import { analyticsPanelSx } from '../../styles/analyticsMsiStyles';
import { attributeOptionKey } from '../../utils/positionSizingAttributeContract';
import { buildPositionSizingOutputHeatmap } from '../../utils/positionSizingOutputHeatmap';
import DataTable from '../data_table/DataTable';
import { sortByNumber, sortByText } from '../data_table/sorting';

const EMPTY_ROW_COUNT = 20;
const PINNED_COLUMNS = Object.freeze(['category']);
const OUTPUT_FIELDS = Object.freeze([
    'gmv',
    'net_rank',
    'exp_dollar',
    'pct_gmv_exp_dollar',
    'beta_net_exp',
    'pct_gmv_beta_exp',
]);
const PLACEHOLDER_ROW_CLASS_NAME = 'pointer-events-none';
const getRowClassName = (row) => (row._isPlaceholder ? PLACEHOLDER_ROW_CLASS_NAME : undefined);

export default function PositionSizingAttributeSummaryTable({
    options,
    selectedSummary,
    summaryKey,
    onSummaryChange,
    rows,
    loading,
    height = 380,
}) {
    const scaffoldRows = useMemo(
        () => Array.from({ length: EMPTY_ROW_COUNT }, (_, index) => ({
            id: `attribute-summary-empty-${index}`,
            _isPlaceholder: true,
        })),
        [],
    );
    const isInitialLoading = Boolean(loading && rows.length === 0);
    const rowsForGrid = rows.length ? rows : scaffoldRows;
    const outputHeatmap = useMemo(
        () => buildPositionSizingOutputHeatmap(rows, OUTPUT_FIELDS),
        [rows],
    );
    const columns = useMemo(() => {
        // A right-aligned number column, tinted by the output heat map when
        // it is a calculated output.
        const numeric = (field, header, size) => ({
            id: field,
            header,
            size,
            accessorFn: (row) => row[field],
            sortFn: sortByNumber,
            cell: ({ row: { original } }) => (original._isPlaceholder
                ? null : formatValue(original[field], { field, header })),
            meta: {
                align: 'right',
                cellClassName: OUTPUT_FIELDS.includes(field)
                    ? ({ row }) => outputHeatmapClassName(outputHeatmap, field, row.original.id)
                    : undefined,
            },
        });
        return [
            {
                id: 'category',
                header: 'Category',
                size: 170,
                accessorFn: (row) => row.category,
                sortFn: sortByText,
                cell: ({ row: { original } }) => (original._isPlaceholder ? null : original.category ?? ''),
            },
            numeric('count', 'Count', 90),
            numeric('gmv', 'GMV', 120),
            numeric('net_rank', 'Net rank', 110),
            numeric('exp_dollar', 'Exp($)', 120),
            numeric('pct_gmv_exp_dollar', '%GMV', 110),
            numeric('beta_net_exp', 'Beta net exp.', 125),
            numeric('pct_gmv_beta_exp', '%GMV (beta)', 125),
        ];
    }, [outputHeatmap]);

    const handleDownload = () => {
        const exportRows = rows.map((row) => ({
            Attribute: selectedSummary?.display_name || 'Attribute',
            Category: row.category,
            Count: row.count ?? '',
            GMV: row.gmv ?? '',
            'Net rank': row.net_rank ?? '',
            'Exp($)': row.exp_dollar ?? '',
            '%GMV': row.pct_gmv_exp_dollar ?? '',
            'Beta net exp.': row.beta_net_exp ?? '',
            '%GMV (beta)': row.pct_gmv_beta_exp ?? '',
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Summary by attribute');
        XLSX.writeFile(workbook, 'Position_sizing_summary_by_attribute.xlsx');
    };

    return (
        <BusyOverlay show={loading && !isInitialLoading} minHeight={height + 76} spinnerSize={24}>
            <Paper
                className={cardClasses}
                elevation={1}
                sx={{ minWidth: 0, ...analyticsPanelSx, ...compactPanelPaddingSx }}
            >
                <CompactPanelHeader
                    title="Summary by Attribute"
                    description="Review capital, exposure, and beta across Watchlist categories."
                >
                    <FormControl size="small" sx={{ minWidth: 190, ...compactSelectSx }}>
                        <InputLabel id="position-sizing-summary-attribute-label">
                            Summary attribute
                        </InputLabel>
                        <Select
                            labelId="position-sizing-summary-attribute-label"
                            label="Summary attribute"
                            value={summaryKey}
                            onChange={(event) => onSummaryChange(event.target.value)}
                            disabled={loading}
                        >
                            {(options || []).filter(
                                (option) => option.attribute_definition_id != null,
                            ).map((option) => {
                                const key = attributeOptionKey(
                                    option.attribute_definition_id,
                                );
                                return (
                                    <MenuItem key={key} value={key}>
                                        {option.display_name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <Tooltip title="Download attribute summary">
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleDownload}
                                aria-label="Download attribute summary"
                                sx={compactIconButtonSx}
                                disabled={loading || rows.length === 0}
                            >
                                <FileDownloadOutlinedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </CompactPanelHeader>
                <DataTable
                    aria-label="Summary by attribute"
                    data={rowsForGrid}
                    columns={columns}
                    pinnedColumns={PINNED_COLUMNS}
                    getRowClassName={getRowClassName}
                    loading={isInitialLoading}
                    height={height}
                />
            </Paper>
        </BusyOverlay>
    );
}

PositionSizingAttributeSummaryTable.propTypes = {
    options: PropTypes.array.isRequired,
    selectedSummary: PropTypes.object,
    summaryKey: PropTypes.string.isRequired,
    onSummaryChange: PropTypes.func.isRequired,
    rows: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    height: PropTypes.number,
};
